import { useTestStore, TV_TESTS, MOBILE_TESTS } from '@/store/useTestStore';
import { t } from '@/i18n/translations';
import { useDeviceInfo } from '@/hooks/useDeviceInfo';
import { DeviceInfo } from './DeviceInfo';
import { ScoreChip } from './ScoreChip';

const TV_NAV: { id: string; key: string }[] = [
  { id: 'tv-list', key: 'tv.list.title' },
  { id: 'tv-css', key: 'tv.css.title' },
  { id: 'tv-compute', key: 'tv.compute.title' },
  { id: 'tv-memory', key: 'tv.memory.title' },
  { id: 'tv-canvas2d', key: 'tv.canvas.title' },
  { id: 'tv-webgl', key: 'tv.webgl.title' },
  { id: 'tv-events', key: 'tv.events.title' },
  { id: 'tv-network', key: 'tv.network.title' },
];

const MOBILE_NAV: { id: string; key: string }[] = [
  { id: 'mobile-touch', key: 'mobile.touch.title' },
  { id: 'mobile-viewport', key: 'mobile.viewport.title' },
  { id: 'mobile-scroll', key: 'mobile.scroll.title' },
  { id: 'mobile-media', key: 'mobile.media.title' },
  { id: 'mobile-battery', key: 'mobile.battery.title' },
  { id: 'mobile-pwa', key: 'mobile.pwa.title' },
  { id: 'mobile-form', key: 'mobile.form.title' },
  { id: 'mobile-network', key: 'mobile.network.title' },
];

export function Sidebar() {
  const {
    language, deviceMode, activeView, results, autoRunning,
    setLanguage, setDeviceMode, setActiveView, startAutoRun, getAutoProgress,
  } = useTestStore();

  const info = useDeviceInfo();
  const nav = deviceMode === 'tv' ? TV_NAV : MOBILE_NAV;
  const tests = deviceMode === 'tv' ? TV_TESTS : MOBILE_TESTS;

  return (
    <div className="bg-gradient-to-b from-white/[.02] to-transparent border border-border rounded-[18px] shadow-lg overflow-hidden flex flex-col min-h-[320px]">
      {/* Header */}
      <div className="p-3.5 pb-2.5 border-b border-border bg-white/[.02]">
        <div className="flex items-center justify-between mb-2">
          <h1 className="text-lg font-black tracking-tight">{t('app.title', language)}</h1>
          <button
            onClick={() => setLanguage(language === 'pl' ? 'en' : 'pl')}
            className="text-xs font-mono border border-border rounded-lg px-2 py-1 hover:border-accent/50 transition-colors"
          >
            {language === 'pl' ? 'EN' : 'PL'}
          </button>
        </div>

        {/* Device mode toggle */}
        <div className="flex gap-2 mb-2">
          <button
            onClick={() => setDeviceMode('tv')}
            disabled={autoRunning}
            className={`flex-1 text-sm font-bold py-2 rounded-xl border transition-all ${
              deviceMode === 'tv'
                ? 'border-accent/65 bg-accent/10 text-accent'
                : 'border-border bg-white/[.02] text-muted hover:border-white/20'
            } disabled:opacity-50`}
          >
            {t('mode.tv', language)}
          </button>
          <button
            onClick={() => setDeviceMode('mobile')}
            disabled={autoRunning}
            className={`flex-1 text-sm font-bold py-2 rounded-xl border transition-all ${
              deviceMode === 'mobile'
                ? 'border-accent/65 bg-accent/10 text-accent'
                : 'border-border bg-white/[.02] text-muted hover:border-white/20'
            } disabled:opacity-50`}
          >
            {t('mode.mobile', language)}
          </button>
        </div>

        <DeviceInfo info={info} />

        {/* Run all button */}
        <button
          onClick={startAutoRun}
          disabled={autoRunning}
          className="w-full mt-2.5 border border-accent/55 bg-accent/[.14] text-white font-bold py-3 rounded-xl hover:bg-accent/20 disabled:opacity-50 transition-all text-base"
        >
          {autoRunning
            ? `${t('overview.running', language)} ${getAutoProgress()}`
            : t('overview.runAll', language)}
        </button>
      </div>

      {/* Navigation */}
      <div className="p-3 flex flex-col gap-2.5 overflow-auto flex-1">
        {/* Overview */}
        <button
          onClick={() => setActiveView('overview')}
          disabled={autoRunning && activeView !== 'overview'}
          className={`w-full text-left border rounded-[14px] px-3 py-3 text-base font-bold flex items-center justify-between gap-2.5 transition-all ${
            activeView === 'overview'
              ? 'border-accent/65 bg-accent/[.08]'
              : 'border-border bg-white/[.02] hover:border-white/20 hover:bg-white/[.03]'
          } disabled:opacity-55 disabled:cursor-not-allowed focus:outline-2 focus:outline-accent/35 focus:outline-offset-2`}
        >
          {t('nav.overview', language)}
        </button>

        {/* Test buttons */}
        {nav.map((item, _i) => {
          const testId = tests[nav.indexOf(item)] as keyof typeof results;
          const result = results[testId];
          return (
            <button
              key={item.id}
              onClick={() => setActiveView(item.id)}
              disabled={autoRunning && activeView !== item.id}
              className={`w-full text-left border rounded-[14px] px-3 py-3 text-[15px] font-bold flex items-center justify-between gap-2.5 transition-all ${
                activeView === item.id
                  ? 'border-accent/65 bg-accent/[.08]'
                  : 'border-border bg-white/[.02] hover:border-white/20 hover:bg-white/[.03]'
              } disabled:opacity-55 disabled:cursor-not-allowed focus:outline-2 focus:outline-accent/35 focus:outline-offset-2`}
            >
              <span className="truncate">{t(item.key, language)}</span>
              <ScoreChip score={result?.score ?? null} />
            </button>
          );
        })}

        {/* Summary */}
        <button
          onClick={() => setActiveView('summary')}
          disabled={autoRunning && activeView !== 'summary'}
          className={`w-full text-left border rounded-[14px] px-3 py-3 text-base font-bold flex items-center justify-between gap-2.5 transition-all ${
            activeView === 'summary'
              ? 'border-accent/65 bg-accent/[.08]'
              : 'border-border bg-white/[.02] hover:border-white/20 hover:bg-white/[.03]'
          } disabled:opacity-55 disabled:cursor-not-allowed focus:outline-2 focus:outline-accent/35 focus:outline-offset-2`}
        >
          {t('nav.summary', language)}
        </button>
      </div>
    </div>
  );
}
